import React, { FC, useEffect, useState } from "react"
import { RouteComponentProps } from "@reach/router"
import _ from "lodash"
import Isvg from 'react-inlinesvg'
import { useMutation } from '@apollo/client'
import toggles from 'feature-toggles'
// Data
import { local } from 'data/lib/local-json'
import usersService from "data/user"
import keyService, * as chord from "data/key"
import automationService from "data/automation"
import validationService from 'data/validate'
// Components, Icons
import { AddUserIcon, UserSubscriptionIcon } from "images/icons/tools"
import { Errors } from "components/asset-management/errors"
import { InputText_s } from 'styles/input-styles'
import Modal from 'components/modal/basic-modal'
import { Toolbar } from "components/subnav"
import UserTable from "components/user-admin/users-table"
import ConfirmationAutomation from 'components/modal/confirmation-automation'
import { WithTip } from 'components/tooltip/with-tip'
// Styles
import { Button_s } from 'styles/app-styles'
import { FormSection } from 'styles/form-styles';
import { Users_s } from "components/user-admin/user-styles"

const Users: FC<RouteComponentProps> = (props) => {
  const [users, setUsers] = useState<object[]>([])
  const [showModal, toggleModal] = useState<boolean>(false);
  const [confirmAdd, toggleConfirmAdd] = useState<boolean>(false);
  const [fullName, setName] = useState<string>("");
  const [email, setEmail] = useState<string>("");
  const [refreshOrgSubscription, { data }] = useMutation(usersService.RefreshOrgSubscription);
  const [gqlError, setGqlError] = useState<string>("");
  const tableId = "user-table"
  const profile = local("auth.profile")
  const id = profile.organizationId;
  const [_errors, setErrors] = useState<any>()

  useEffect(() => {
    const filter = (errors):object => {
      return {
        ...errors,
        errordetail: _.pick(errors.errordetail, 'user'),
        calcDetail: undefined
      }
    }
    const validationSubscription = validationService.stream().subscribe((e) => {
      setErrors(e)
    })
    validationService.addFilter(filter)
    validationService.validate('user')

    return () => {
      validationSubscription.unsubscribe()
      validationService.removeFilter(filter)
    }
  },[])

  useEffect(() => {
    const usersSubscription = usersService.stream().subscribe(data => {
      let users = data['users'].map(user => { return user });
      if (data.error) {
        setGqlError(data.error)
      }
      setUsers(users);
    })

    return function cleanup() {
      usersSubscription.unsubscribe()
    }
  }, [])

  useEffect(() => {
    usersService.getUsers()
  }, [id])

  useEffect(() => {
    keyService.stream().subscribe(k => {
      if (k.chord === chord.ESC && showModal) cancelModal()
    })
  }, [showModal])

  const Add = () => {
    return (
      <WithTip position="bottom">
        Add User
        <a onClick={()=>toggleShow()}>
          <Isvg src={AddUserIcon} width="20px" />
        </a>
      </WithTip>
    )
  }

  const Subscription = () => {
    return (
      <WithTip position="bottom">
        Manage Subscription
        <a onClick={manageSubscription}>
          <Isvg src={UserSubscriptionIcon} width="20px" />
        </a>
      </WithTip>
    )
  }

  const manageSubscription = (e) => {
    const profile = local("auth.profile")
    const id      = profile.organizationId;
    const cbInstance = window.Chargebee.getInstance()
    cbInstance.setPortalCallbacks({
      close: () => {
        refreshOrgSubscription({
          variables: { id },
          refetchQueries: [{ query: usersService.GetOrganization, variables: { id } }]
        })
        .catch(error => setGqlError(_.capitalize(_.get(error, 'graphQLErrors[0].message'))))
      }
    })
    const portal = cbInstance.createChargebeePortal()
    portal.open()
  }

  const submitNewUser = () => {
    usersService.createUser(fullName, email);
    toggleConfirmAdd(true);
  }

  const toggleShow = () => {
    if (showModal){
      cancelModal()
    } else {
      toggleModal(true)
      automationService.broadcast("MODAL_OPENING", {})
    }
  } 

  const cancelModal = () => {
    toggleModal(false)
    toggleConfirmAdd(false)
    setName("")
    setEmail("")
    setTimeout(() => automationService.broadcast("MODAL_CLOSING", {}), 200)
    setGqlError('')
  }

  return (
    <div>
      <Users_s>
        <Toolbar>
          <Add />
          { toggles.displayChargebee() && <Subscription /> }
          <Errors />
        </Toolbar>
        { showModal && <Modal show={showModal} title="Add New User" toggleShow={toggleShow} width={300}>
          { confirmAdd ? <FormSection id="addUserModal"><div>
              <p>{gqlError ? gqlError : `${fullName} was added to the organization. An email invitation will be sent to your new user.`}</p>
            </div>
            <div style={{justifyContent: 'end'}}>
              <Button_s onClick={cancelModal}>Close</Button_s>
            </div>
          </FormSection>
          : <FormSection id="addUserModal">
            <div>
              <label htmlFor="nameInput">Full Name</label>
              <InputText_s className="automated" id="nameInput" value={fullName} onChange={(e) => setName((e.target as HTMLInputElement).value)} />
            </div>
            <div>
            <label htmlFor="emailInput">Email</label>
              <InputText_s className="automated" id="emailInput" value={email} onChange={(e) => setEmail((e.target as HTMLInputElement).value)} />
            </div>
            <div>
              <Button_s onClick={cancelModal}>Cancel</Button_s>
              <Button_s primary onClick={()=>submitNewUser()}>
                Add User
              </Button_s>
            </div>          
          </FormSection> }
          <ConfirmationAutomation />
        </Modal> }
        { users && <UserTable tableId={tableId} data={users} /> }
      </Users_s>
    </div>
  )
}

export default Users
